import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = [
  "Denials",
  "Revenue Recovery",
  "Revenue Forecasting",
  "KPIs & Analytics",
  "Payor Performance",
  "AI & Automation",
  "Leadership",
  "Operational Excellence",
];

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "Denials",
  tags: [],
  cover_image: "",
  author_name: "Revenue Cycle IQ",
  author_title: "Healthcare Revenue Cycle Consulting",
  status: "draft",
  featured: false,
  seo_description: "",
  key_takeaways: [],
};

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editMode = Boolean(id);

  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(editMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [aiBusy, setAiBusy] = useState({ polish: false, tags: false, summary: false, seo: false });
  const [aiInstructions, setAiInstructions] = useState("");
  const [polishOriginal, setPolishOriginal] = useState(null);

  useEffect(() => {
    if (!editMode) return;
    setLoading(true);
    api.get("/articles/admin/all")
      .then(({ data }) => {
        const found = (data.items || []).find((a) => a.id === id);
        if (found) setData({ ...EMPTY, ...found, tags: found.tags || [] });
        else setError("Article not found.");
      })
      .finally(() => setLoading(false));
  }, [id, editMode]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addTag = (raw) => {
    const t = (raw || tagInput).trim();
    if (!t) return;
    if (!data.tags.includes(t)) update("tags", [...data.tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => update("tags", data.tags.filter((x) => x !== t));

  const save = async (statusOverride) => {
    setError(""); setSaving(true);
    try {
      const payload = { ...data, status: statusOverride || data.status };
      let result;
      if (editMode) {
        const { data: r } = await api.put(`/articles/${id}`, payload);
        result = r;
        setData({ ...EMPTY, ...r, tags: r.tags || [] });
      } else {
        const { data: r } = await api.post(`/articles`, payload);
        result = r;
        navigate(`/admin/edit/${r.id}`, { replace: true });
      }
      return result;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const runPolish = async () => {
    if (!data.content.trim()) return setError("Add some draft content first.");
    setAiBusy((b) => ({ ...b, polish: true })); setError("");
    try {
      const { data: r } = await api.post("/ai/polish", {
        content: data.content,
        instructions: aiInstructions,
      });
      setPolishOriginal(data.content);
      update("content", r.content);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAiBusy((b) => ({ ...b, polish: false }));
    }
  };

  const undoPolish = () => {
    if (polishOriginal != null) { update("content", polishOriginal); setPolishOriginal(null); }
  };

  const runTags = async () => {
    if (!data.title || !data.content.trim()) return setError("Add title and content first.");
    setAiBusy((b) => ({ ...b, tags: true })); setError("");
    try {
      const { data: r } = await api.post("/ai/tags", { title: data.title, content: data.content });
      const merged = Array.from(new Set([...(data.tags || []), ...(r.tags || [])]));
      update("tags", merged);
      if (r.category && CATEGORIES.includes(r.category)) update("category", r.category);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAiBusy((b) => ({ ...b, tags: false }));
    }
  };

  const runSummary = async () => {
    if (!data.content.trim()) return setError("Add some content first.");
    setAiBusy((b) => ({ ...b, summary: true })); setError("");
    try {
      const { data: r } = await api.post("/ai/summary", { content: data.content });
      update("excerpt", r.summary);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAiBusy((b) => ({ ...b, summary: false }));
    }
  };

  const runSEO = async () => {
    if (!data.title || !data.content.trim()) return setError("Add title and content first.");
    setAiBusy((b) => ({ ...b, seo: true })); setError("");
    try {
      const { data: r } = await api.post("/ai/seo", { title: data.title, content: data.content });
      update("seo_description", r.description);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAiBusy((b) => ({ ...b, seo: false }));
    }
  };

  const wordCount = useMemo(() => (data.content || "").trim().split(/\s+/).filter(Boolean).length, [data.content]);

  if (loading) return <div className="p-10 text-[var(--rciq-muted)]">Loading…</div>;

  return (
    <div data-testid="admin-editor" className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link to="/admin" className="text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 hover:text-[var(--rciq-ink)]" data-testid="editor-back">
            ← Articles
          </Link>
          <h1 className="font-serif-display text-3xl md:text-4xl font-700 mt-2">
            {editMode ? "Edit article" : "Write new article"}
          </h1>
        </div>
        <div className="flex gap-3 items-center">
          <button onClick={() => setShowPreview((p) => !p)} className="btn-pill btn-pill-ghost text-[12px]" data-testid="toggle-preview">
            {showPreview ? "Hide preview" : "Preview"}
          </button>
          <button onClick={() => save("draft")} disabled={saving} className="btn-pill btn-pill-ghost text-[12px]" data-testid="save-draft">
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button onClick={() => save("published")} disabled={saving} className="btn-pill btn-pill-primary text-[12px]" data-testid="publish-article">
            {data.status === "published" ? "Update & republish" : "Publish"}
          </button>
        </div>
      </div>

      {error && <div data-testid="editor-error" className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[var(--rciq-line)] rounded-2xl p-6">
            <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 mb-2">Title</label>
            <input
              data-testid="editor-title"
              value={data.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="What slowed cash this quarter…"
              className="w-full text-2xl md:text-3xl font-serif-display font-700 border-none outline-none placeholder:text-[var(--rciq-muted)]/60"
            />

            <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 mb-2 mt-6">Excerpt / TL;DR</label>
            <textarea
              data-testid="editor-excerpt"
              value={data.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
              rows={3}
              placeholder="A 1–2 sentence hook that appears on cards and at the top of the article."
              className="editor-textarea"
            />
            <div className="mt-2 flex justify-end">
              <button onClick={runSummary} disabled={aiBusy.summary} className="text-[11px] uppercase tracking-[0.12em] font-700 text-[var(--rciq-teal-dark)] hover:underline" data-testid="ai-summary-btn">
                {aiBusy.summary ? "Generating…" : "Generate from content"}
              </button>
            </div>
          </div>

          <div className="bg-white border border-[var(--rciq-line)] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700">
                Article content <span className="normal-case tracking-normal text-[var(--rciq-muted)]/80 font-400">(Markdown supported)</span>
              </label>
              <span className="text-[11px] text-[var(--rciq-muted)]">{wordCount} words</span>
            </div>
            <textarea
              data-testid="editor-content"
              value={data.content}
              onChange={(e) => { update("content", e.target.value); if (polishOriginal) setPolishOriginal(null); }}
              rows={22}
              placeholder={"Paste or write your draft here.\n\n## Use headings like this\n\nUse **bold** for emphasis and - bullet points for lists."}
              className="editor-textarea font-mono"
              style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}
            />

            {/* Polish controls */}
            <div className="mt-4 p-4 rounded-xl bg-[var(--rciq-bg-tint)] border border-[var(--rciq-line)]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700">Polish & Format</div>
                {polishOriginal && (
                  <button onClick={undoPolish} className="text-[11px] uppercase tracking-[0.12em] font-700 text-[var(--rciq-muted)] hover:text-[var(--rciq-ink)]" data-testid="undo-polish">
                    Undo polish
                  </button>
                )}
              </div>
              <p className="text-xs text-[var(--rciq-muted)] mb-3">
                Cleans up grammar, formatting and flow without changing what you said. Your voice stays intact.
              </p>
              <textarea
                data-testid="polish-instructions"
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                rows={2}
                placeholder="Optional editing notes (e.g., 'keep it punchy, no marketing speak')"
                className="editor-textarea text-sm"
              />
              <button onClick={runPolish} disabled={aiBusy.polish} className="mt-3 btn-pill btn-pill-ghost text-[11px]" data-testid="polish-btn">
                {aiBusy.polish ? "Polishing…" : "Polish my draft"}
              </button>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-white border border-[var(--rciq-line)] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700">Key Takeaways</label>
              <span className="text-[11px] text-[var(--rciq-muted)]">{data.key_takeaways.length} / 6</span>
            </div>
            <p className="text-xs text-[var(--rciq-muted)] mb-3">
              Shown in a highlighted box at the top of the article. 3–5 short bullets work best.
            </p>
            <div className="space-y-2">
              {data.key_takeaways.map((kt, idx) => (
                <div key={idx} className="flex gap-2 items-start" data-testid={`takeaway-row-${idx}`}>
                  <span className="mt-3 text-[var(--rciq-teal-dark)] font-700 text-sm">{idx + 1}.</span>
                  <input
                    data-testid={`takeaway-${idx}`}
                    value={kt}
                    onChange={(e) => {
                      const next = [...data.key_takeaways];
                      next[idx] = e.target.value;
                      update("key_takeaways", next);
                    }}
                    className="input-field flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => update("key_takeaways", data.key_takeaways.filter((_, i) => i !== idx))}
                    className="mt-2 text-[var(--rciq-muted)] hover:text-red-500 text-xs font-700"
                    data-testid={`takeaway-remove-${idx}`}
                    aria-label="Remove takeaway"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {data.key_takeaways.length < 6 && (
                <button
                  type="button"
                  onClick={() => update("key_takeaways", [...data.key_takeaways, ""])}
                  className="mt-2 text-[11px] uppercase tracking-[0.12em] font-700 text-[var(--rciq-teal-dark)] hover:underline"
                  data-testid="takeaway-add"
                >
                  + Add takeaway
                </button>
              )}
            </div>
          </div>

          {showPreview && (
            <div className="bg-white border border-[var(--rciq-line)] rounded-2xl p-8" data-testid="content-preview">
              <div className="eyebrow mb-4">Preview</div>
              <h2 className="font-serif-display text-3xl font-700 mb-4">{data.title || "Untitled"}</h2>
              {data.excerpt && <p className="text-[var(--rciq-ink-2)] mb-6">{data.excerpt}</p>}
              <div className="prose-rciq">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.content || "_Start writing to preview…_"}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-white border border-[var(--rciq-line)] rounded-2xl p-6 space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 mb-2">Category</label>
              <select data-testid="editor-category" value={data.category} onChange={(e) => update("category", e.target.value)} className="input-field">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700">Tags</label>
                <button onClick={runTags} disabled={aiBusy.tags} className="text-[11px] uppercase tracking-[0.12em] font-700 text-[var(--rciq-teal-dark)] hover:underline" data-testid="ai-tags-btn">
                  {aiBusy.tags ? "Suggesting…" : "Suggest"}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {data.tags.map((t) => (
                  <span key={t} className="tag-pill" data-testid={`editor-tag-${t}`}>
                    {t}
                    <button onClick={() => removeTag(t)} className="ml-1.5 text-[var(--rciq-teal-dark)] hover:text-red-500" aria-label={`Remove ${t}`}>×</button>
                  </span>
                ))}
              </div>
              <input
                data-testid="editor-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Type a tag and press Enter"
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 mb-2">Cover image URL</label>
              <input
                data-testid="editor-cover-image"
                value={data.cover_image}
                onChange={(e) => update("cover_image", e.target.value)}
                placeholder="https://…"
                className="input-field"
              />
              {data.cover_image && (
                <img src={data.cover_image} alt="cover" className="mt-3 rounded-lg w-full aspect-[16/9] object-cover" />
              )}
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 mb-2">Slug</label>
              <input
                data-testid="editor-slug"
                value={data.slug}
                onChange={(e) => update("slug", e.target.value)}
                placeholder="auto-generated from title"
                className="input-field"
              />
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="editor-featured"
                  type="checkbox"
                  checked={!!data.featured}
                  onChange={(e) => update("featured", e.target.checked)}
                  className="accent-[var(--rciq-teal)] w-4 h-4"
                />
                <span className="text-sm font-600">Feature on Insights home</span>
              </label>
            </div>
          </div>

          <div className="bg-white border border-[var(--rciq-line)] rounded-2xl p-6 space-y-4">
            <div className="eyebrow">Author</div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 mb-2">Name</label>
              <input data-testid="editor-author-name" value={data.author_name} onChange={(e) => update("author_name", e.target.value)} className="input-field" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700 mb-2">Title</label>
              <input data-testid="editor-author-title" value={data.author_title} onChange={(e) => update("author_title", e.target.value)} className="input-field" />
            </div>
          </div>

          <div className="bg-white border border-[var(--rciq-line)] rounded-2xl p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="eyebrow">SEO</div>
              <button onClick={runSEO} disabled={aiBusy.seo} className="text-[11px] uppercase tracking-[0.12em] font-700 text-[var(--rciq-teal-dark)] hover:underline" data-testid="ai-seo-btn">
                {aiBusy.seo ? "Generating…" : "Generate"}
              </button>
            </div>
            <textarea
              data-testid="editor-seo"
              value={data.seo_description}
              onChange={(e) => update("seo_description", e.target.value)}
              rows={3}
              placeholder="Meta description (140–160 chars)"
              className="editor-textarea text-sm"
            />
            <div className="text-[11px] text-[var(--rciq-muted)]">{(data.seo_description || "").length} chars</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
